if (!customElements.get('ac-media-editorial')) {
  class AcMediaEditorial extends HTMLElement {
    connectedCallback() {
      if (this.controller && !this.controller.signal.aborted) return;
      this.controller = new AbortController();
      this.animations = new Set();
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      const signal = this.controller.signal;
      this.decorObserver = new ResizeObserver(() => this.positionDecoration());
      this.decorObserver.observe(this);
      const heading = this.closest('.ac-media-editorial')?.querySelector('.ac-media-editorial__heading');
      for (const span of heading?.children || []) this.decorObserver.observe(span);
      for (const icon of this.querySelectorAll('.ac-media-editorial__icon')) {
        icon.addEventListener('load', () => this.positionDecoration(), { signal });
      }
      const cancel = () => { this.animations.forEach(animation => animation.cancel()); this.animations.clear(); };
      let previousReduced = this.reducedMotion;
      const syncMotion = () => {
        const reduced = this.reducedMotion;
        if (reduced === previousReduced) return;
        previousReduced = reduced;
        cancel();
        this.setupPin();
      };
      this.motion.addEventListener('change', syncMotion, { signal });
      this.motionObserver = new MutationObserver(syncMotion);
      this.motionObserver.observe(document.body, { attributes: true, attributeFilter: ['data-ac-motion'] });
      document.addEventListener('shopify:section:reorder', () => this.setupPin(), { signal });
      const ready = () => document.fonts.ready.then(() => {
        if (!signal.aborted && this.isConnected) { this.positionDecoration(); this.setupPin(); }
      });
      if (window.ScrollTrigger || document.readyState === 'complete') ready();
      else document.addEventListener('DOMContentLoaded', ready, { once: true, signal });
      this.querySelectorAll('.ac-media-editorial__card').forEach(card => {
        const play = () => {
          if (this.reducedMotion || this.dataset.hover === 'false' || !matchMedia('(min-width: 1025px) and (hover: hover)').matches || card.getAnimations({ subtree: true }).length) return;
          const [first, second] = card.querySelectorAll('.ac-media-editorial__image');
          if (!first || !second) return;
          const middle = 350 / 650;
          const ease = 'cubic-bezier(.4, 0, .6, 1)';
          const options = { duration: 650 };
          const animations = [
            first.animate([
              { transform: 'scaleX(1)', transformOrigin: 'left center', offset: 0, easing: ease },
              { transform: 'scaleX(0)', transformOrigin: 'left center', offset: middle },
              { transform: 'scaleX(0)', transformOrigin: 'right center', offset: middle, easing: ease },
              { transform: 'scaleX(1)', transformOrigin: 'right center', offset: 1 }
            ], options),
            second.animate([
              { transform: 'scaleX(0)', transformOrigin: 'right center', offset: 0, easing: ease },
              { transform: 'scaleX(1)', transformOrigin: 'right center', offset: middle },
              { transform: 'scaleX(1)', transformOrigin: 'left center', offset: middle, easing: ease },
              { transform: 'scaleX(0)', transformOrigin: 'left center', offset: 1 }
            ], options)
          ];
          animations.forEach(animation => {
            this.animations.add(animation);
            animation.finished.catch(() => {}).finally(() => this.animations.delete(animation));
          });
        };
        card.addEventListener('pointerenter', event => { if (event.pointerType === 'mouse') play(); }, { signal });
        card.addEventListener('focus', () => { if (card.matches(':focus-visible')) play(); }, { signal });
      });
    }
    get reducedMotion() {
      return this.motion.matches || document.body.dataset.acMotion === 'reduced';
    }

    setupPin() {
      this.pin?.kill();
      this.pin = null;
      const section = this.closest('.ac-media-editorial');
      const main = this.closest('main');
      const footer = document.querySelector('ac-reference-footer');
      if (!section || !footer || this.reducedMotion || !window.ScrollTrigger || !window.gsap) return;
      if ([...main.querySelectorAll('section')].at(-1) !== section) return;
      window.gsap.registerPlugin(window.ScrollTrigger);
      this.pin = window.ScrollTrigger.create({ trigger: section, start: 'bottom bottom', endTrigger: footer, end: 'bottom bottom', pin: true, pinSpacing: false });
    }
    positionDecoration() {
      this.positionCrown();
      const icon = this.querySelector('.ac-media-editorial__icon--second');
      if (!icon) return;
      if (!matchMedia('(min-width: 768px)').matches) { icon.style.removeProperty('top'); icon.style.removeProperty('bottom'); return; }
      const desktop = matchMedia('(min-width: 1024px)').matches;
      const lists = [...this.querySelectorAll('.ac-media-editorial__list')];
      const list = desktop ? lists[0] : lists[1] || lists[0];
      const card = list?.querySelector('.ac-media-editorial__card');
      if (!card) return;
      const frame = card.getBoundingClientRect();
      const container = this.getBoundingClientRect();
      const gap = parseFloat(getComputedStyle(list).rowGap);
      const unit = gap / (desktop ? 1.2 : .7);
      const sourceHeight = frame.width * 268 / 190;
      const offset = sourceHeight + gap - (desktop ? 25.3 : 29.5999) * unit - icon.getBoundingClientRect().height;
      const top = frame.bottom - container.top + offset;
      if (Math.abs(parseFloat(icon.style.top) - top) > .1 || !icon.style.top) icon.style.top = `${top}px`;
      icon.style.bottom = 'auto';
    }
    positionCrown() {
      const crown = this.querySelector('.ac-media-editorial__icon--first');
      const heading = this.closest('.ac-media-editorial')?.querySelector('.ac-media-editorial__heading');
      if (!crown || !heading) return;
      if (!matchMedia('(min-width: 768px)').matches) {
        crown.style.removeProperty('translate');
        crown.style.removeProperty('visibility');
        this.crownShift = 0;
        return;
      }
      const [first, last] = [...heading.children].map(span => span.getBoundingClientRect());
      if (!first || !last) return;
      const frame = crown.getBoundingClientRect();
      const padding = parseFloat(getComputedStyle(heading).columnGap) / 2;
      const minimum = first.right + padding;
      const maximum = last.left - frame.width - padding;
      // Keep the reference anchor unless merchant copy needs its space.
      const origin = frame.left - (this.crownShift || 0);
      const shift = maximum >= minimum ? Math.min(Math.max(origin, minimum), maximum) - origin : 0;
      if (Math.abs(shift - (this.crownShift || 0)) > .1) crown.style.translate = `${shift}px 0`;
      this.crownShift = shift;
      crown.style.visibility = maximum >= minimum ? '' : 'hidden';
    }
    disconnectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected) return;
        this.controller?.abort();
        this.motionObserver?.disconnect();
        this.decorObserver?.disconnect();
        this.pin?.kill();
        this.pin = null;
        this.animations?.forEach(animation => animation.cancel());
        this.animations?.clear();
      });
    }
  }
  customElements.define('ac-media-editorial', AcMediaEditorial);
}
